// AppProvider.tsx
import { IonIcon, IonText, useIonAlert, useIonLoading, useIonRouter, useIonToast } from '@ionic/react';
import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import SWTextLogo from './assets/logos/header-text-logo.png';
import SWTextDarkLogo from './assets/logos/SoloWise-Logo-Full-Horizontal-Dark.png';
import SWFullHorizLogo from './assets/logos/SoloWise-Logo-Full-Horizontal.png';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { Network } from '@capacitor/network';
import { checkmarkOutline, closeOutline, helpOutline, warningOutline } from 'ionicons/icons';

import { configureRevenueCat } from './lib/RevenueCat';
import { getBillingAdapter } from './components/getBillingAdapter';
import { Purchases} from '@revenuecat/purchases-capacitor';

const billing = getBillingAdapter();

interface AppProviderProps {
    children: ReactNode;
    showLoader: any;
    setShowLoader: any;
}

interface AppContextProps {
  networkStatus: any; 
  setNetworkStatus: any;
  appStatus: any; 
  setAppStatus: any;
  appStatusMarkup: any;
  setAppStatusMarkup: any;
  getAppStatus: any;
  authKey: any;
  setAuthKey: any;
  authenticateMember: any;
  authorizedMember: any;
  getAuthorizedMember: any;
  setAuthorizedMember: any;
  accountSetupPct: any;
  setAccountSetupPct: any;
  revenueCatReady: any;
  updateMember: any;

  loading: any;
  setLoading: any

  showLoader: any;
  setShowLoader: any

  level: any;
  getLevel: any;
  setLevel: any;
  levels: any;
  getLevels: any;
  setLevels: any;
  limits: any;
  setLimits: any;
  profiles: any;
  setProfiles: any;
  settings: any;
  getSettings: any;
  setSettings: any;
  alarms: any;
  setAlarms: any;
  countries: any;
  getCountries: any;
  states: any;
  getStates: any;

  activeContactsCount: any;
  setActiveContactsCount: any;
  activeKidsCount: any;
  setActiveKidsCount: any;
  activePartnershipsCount: any;
  setActivePartnershipsCount: any;
  activePetsCount: any;
  setActivePetsCount: any;
  activePlacesCount: any;
  setActivePlacesCount: any;
  activeRoutesCount: any;
  setActiveRoutesCount: any;
  activeVehiclesCount: any;
  setActiveVehiclesCount: any;
  monthlyAlertsCount: any;
  setMonthlyAlertsCount: any;

  places: any;
  savedPlaces: any;
  startPlaces: any;
  endPlaces: any;
  setStartPlaces: any;
  setEndPlaces: any;
  getPlaces: any;
  setPlaces: any;
  getSavedPlaces: any;
  setSavedPlaces: any;
  routes: any;
  savedRoutes: any;
  getRoutes: any;
  setRoutes: any;
  getSavedRoutes: any;
  setSavedRoutes: any;
  activePartners: any;
  pendingPartners: any;
  getPartnerships: any;
  setActivePartners: any;
  setPendingPartners: any;
  kids: any;
  getKids: any;
  setKids: any;
  availableKids: any;
  setAvailableKids: any;
  pets: any;
  getPets: any;
  setPets: any;
  availablePets: any;
  setAvailablePets: any;
  vehicles: any;
  vehiclesAll: any;
  setVehiclesAll: any;
  vehiclesStart: any;
  setVehiclesStart: any;
  selectedVehiclesStart: any;
  setSelectedVehiclesStart: any;
  vehiclesEnroute: any;
  setVehiclesEnroute: any;
  selectedVehiclesEnroute: any;
  setSelectedVehiclesEnroute: any;
  vehiclesEnd: any;
  setVehiclesEnd: any;
  selectedVehiclesEnd: any;
  setSelectedVehiclesEnd: any;
  getVehicles: any;
  setVehicles: any;
  contacts: any;
  getContacts: any;
  setContacts: any;
  availableContacts: any;
  setAvailableContacts: any;
  checkedContacts: any;
  setCheckedContacts: any;
  locationPerm: any;
  setLocationPerm: any;
  locationPermStr: any;
  setLocationPermStr: any;
  pushPermStr: any;
  setPushPermStr: any;
  now: any;
  setNow: any;
  currentTimer: any;
  setCurrentTimer: any;
  currentTimerEnd: any;
  setCurrentTimerEnd: any;
  currentTimerStatus: any; 
  setCurrentTimerStatus: any;
  currentTimers: any;
  setCurrentTimers: any;
  getCurrentTimers: any;
  pendingTimer: any;
  setPendingTimer: any;
  fullLogoLoaded: any;
  textLogoLoaded: any;
  setFullLogoLoaded: any;
  setTextLogoLoaded: any;
  fadeInFullLogo: any;
  fadeInTextLogo: any;
  fadeInImage: any;
  nl2br: any;
  clearAllCookies: any;
  SWTextLogo: any;
  SWTextDarkLogo: any;
  SWFullHorizLogo: any;
  timezones: any;
  timezonesIANA: any;
  timezoneName: any;
  timezoneIANAName: any;
  getTimezones: any;
  getTimezonesIANA: any;
  setTimezones: any;
  userTimezone: any;
  userIANATimezone: any;
  getUserTimezone: any;
  getUserIANATimezone: any;
  setUserTimezone: any;
  setUserIANATimezone: any;
  monthNames: any;
  Preferences: any;
  swipeEnabled: any;
  setSwipeEnabled: any;
  showToast: any;
  present: any;
  dismiss: any;
}

const AppContext = createContext<AppContextProps | undefined>(undefined);

type Plan = {
  memberLevelID: string;
  memberLevelStatus: string;
  memberLevelName: string;
  memberLevelDesc: string;
  memberLevelPrice: string;
  memberLevelTier: number; 
  memberLevelRCEntitlement: string;  // used to detect current plan
  memberLevelRCPackage: string;      // joins to RC package.identifier
  memberLevelTimersText: string;
  memberLevelTimersInt: number;
  memberLevelContacts: number;
  memberLevelPlaces: number;
  memberLevelRoutes: number;
  memberLevelKids: number;
  memberLevelPets: number;
  memberLevelVehicles: number;
  memberLevelPartners: number;
};

const isNative = () => {
  const p = Capacitor.getPlatform();
  return p === 'android' || p === 'ios';
};

export const AppProvider: React.FC<AppProviderProps> = ({ children, showLoader, setShowLoader }) => {
  const [networkStatus, setNetworkStatus] = useState<boolean>(true);
  const [appStatus, setAppStatus] = useState<string>('Unknown');
  const [appStatusMarkup, setAppStatusMarkup] = useState<ReactNode>(<IonIcon icon={helpOutline}></IonIcon>);      
  const [now, setNow] = useState<number>(Date.now());  
  const [loading, setLoading] = useState<boolean>(true);
  const [authKey, setAuthKey] = useState<any>({});
  const [authorizedMember, setAuthorizedMember] = useState<any>({});  
  const [accountSetupPct, setAccountSetupPct] = useState<number>(0);
  const [alarms, setAlarms] = useState<number>(0);
  const [level, setLevel] = useState<any>({});
  const [levels, setLevels] = useState<any>([]);
  const [catalog, setCatalog] = useState<any>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [currentPlanKey, setCurrentPlanKey] = useState<string>('1');
  const [limits, setLimits] = useState<any>({});
  const [profiles, setProfiles] = useState<any>([]);
  const [settings, setSettings] = useState<any>({});  
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [timezones, setTimezones] = useState([]);
  const [timezonesIANA, setTimezonesIANA] = useState([]);
  const [userTimezone, setUserTimezone] = useState<string>('');
  const [userIANATimezone, setUserIANATimezone] = useState<string>('');
  const [places, setPlaces] = useState<any[]>([]);
  const [savedPlaces, setSavedPlaces] = useState<any[]>([]);
  const [startPlaces, setStartPlaces] = useState<any[]>([]);
  const [endPlaces, setEndPlaces] = useState<any[]>([]);
  const [routes, setRoutes] = useState<any[]>([]);  
  const [savedRoutes, setSavedRoutes] = useState<any[]>([]);
  const [activePartners, setActivePartners] = useState<any[]>([]);
  const [pendingPartners, setPendingPartners] = useState<any[]>([]);
  const [kids, setKids] = useState<any[]>([]);
  const [availableKids, setAvailableKids] = useState<any[]>([]);
  const [pets, setPets] = useState<any[]>([]);
  const [availablePets, setAvailablePets] = useState<any[]>([]);
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [vehiclesAll, setVehiclesAll] = useState<any[]>([]);
  const [vehiclesStart, setVehiclesStart] = useState<any[]>([]);
  const [selectedVehiclesStart, setSelectedVehiclesStart] = useState<any[]>([]);
  const [vehiclesEnroute, setVehiclesEnroute] = useState<any[]>([]);
  const [selectedVehiclesEnroute, setSelectedVehiclesEnroute] = useState<any[]>([]);
  const [vehiclesEnd, setVehiclesEnd] = useState<any[]>([]);
  const [selectedVehiclesEnd, setSelectedVehiclesEnd] = useState<any[]>([]);
  const [contacts, setContacts] = useState<any[]>([]);
  const [availableContacts, setAvailableContacts] = useState<any[]>([]);
  const [checkedContacts, setCheckedContacts] = useState<any[]>([]);

  const [activeContactsCount, setActiveContactsCount] = useState<number>(0);
  const [activeKidsCount, setActiveKidsCount] = useState<number>(0);
  const [activePartnershipsCount, setActivePartnershipsCount] = useState<number>(0);
  const [activePetsCount, setActivePetsCount] = useState<number>(0);
  const [activePlacesCount, setActivePlacesCount] = useState<number>(0);
  const [activeRoutesCount, setActiveRoutesCount] = useState<number>(0);
  const [activeVehiclesCount, setActiveVehiclesCount] = useState<number>(0);
  const [monthlyAlertsCount, setMonthlyAlertsCount] = useState<number>(0);
    
  const [fullLogoLoaded, setFullLogoLoaded] = useState<boolean>(false);
  const [textLogoLoaded, setTextLogoLoaded] = useState<boolean>(false);

  const [locationPerm, setLocationPerm] = useState<boolean>(false);
  const [locationPermStr, setLocationPermStr] = useState<string>('');
  const [pushPermStr, setPushPermStr] = useState<string>('');    
    

  const [currentTimers, setCurrentTimers] = useState<any[]>([]);
  const [currentTimer, setCurrentTimer] = useState<any>({}); 
  const [currentTimerEnd, setCurrentTimerEnd] = useState<number>(0);      
  const [currentTimerStatus, setCurrentTimerStatus] = useState<number>(0); 
  const [pendingTimer, setPendingTimer] = useState<any>({}); 

  const [revenueCatReady, setRevenueCatReady] = useState(false);
  
  const [swipeEnabled, setSwipeEnabled] = useState<boolean>(true);
  const [showToast] = useIonToast();  
  const [present, dismiss] = useIonLoading();
  const router = useIonRouter();

  //
  // Listen for Network connection changes
  //
  /*Network.addListener('networkStatusChange', status => {
    //if (status.connected) {
        setNetworkStatus(status.connected);
    //}
  });*/

  useEffect(() => {
    let listenerHandle: { remove: () => Promise<void> } | undefined;

    const setupNetworkListener = async () => {
      const status = await Network.getStatus();
      setNetworkStatus(status.connected);

      listenerHandle = await Network.addListener(
        'networkStatusChange',
        status => {
          setNetworkStatus(status.connected);
        }
      );
    };

    setupNetworkListener();

    return () => {
      listenerHandle?.remove();
    };
  }, []);


  //
  // Get App Status
  //
  const getAppStatus = async () => {
        let initTime = new Date().getTime();
            initTime = Math.round(initTime / 1000);
        const timeoutMs = 3000;
        const controller = new AbortController();
        const id = setTimeout(() => controller.abort(), timeoutMs);
        const formData = new FormData();
        formData.append('mt', authKey.mt);
        try {
            
            const response = await fetch('https://api.besolowise.com/status/', {
                method: 'POST',
                credentials: 'include',
                body: formData,
                signal: controller.signal,
            });
            
            if(response.ok) {                
                const obj = await response.json(); 
                
                if(obj.success) {      
                    let respTime = new Date().getTime();
                        respTime = Math.round(respTime / 1000);
                    let waitTime = respTime - initTime;
                    
                    if(waitTime < 1) {
                        setAppStatus('Fast');
                        setAppStatusMarkup(<IonText><IonIcon icon={checkmarkOutline}></IonIcon> Fast </IonText>);
                        return 'Fast';
                    } else {
                        setAppStatus('Slow');
                        setAppStatusMarkup(<IonText><IonIcon icon={warningOutline}></IonIcon> Slow </IonText>);
                        return 'Slow';
                    }
                } else {
                    setAppStatus('No')
                    setAppStatusMarkup(<IonText><IonIcon icon={closeOutline}></IonIcon> No </IonText>);
                    return 'No';
                } 
            }                    

        } catch(error) {
            clearTimeout(id);
            setAppStatus('No')
            setAppStatusMarkup(<IonText><IonIcon icon={closeOutline}></IonIcon> No </IonText>);
            return 'No';
        }   
  };


  //
  // Authenticate Member
  //
  const authenticateMember = async () => {

    var mt = ''; 
    var tp = '';
    var tt = '0';    
    var rtn = {mt: mt, tp: tp, tt: tt};
    var now = new Date();
    var nowUTC = (now.getTime() / 1000); //  + (now.getTimezoneOffset() * 60)
      
    // ... check if storedAuthKey is set
    const storedAuthKey: any = await Preferences.get({key: 'solowise'}); 
      
   // console.log(authKey, storedAuthKey);

    // First, if state variable 'authKey' is set,
    /*if(authKey.mt) {

        // Parse authKey   
        mt = authKey.mt;
        tp = authKey.tp;
        tt = authKey.tt;
        rtn = authKey;

    } else {
     
    } */
      
      // If storedAuthKey is set, 
      if(storedAuthKey.value !== null) {

        // ... set authKey as storedAuthKey and parse its components
        let parsedAuthKey = JSON.parse(storedAuthKey.value);
        setAuthKey(parsedAuthKey);
        mt = parsedAuthKey.mt;
        tp = parsedAuthKey.tp;
        tt = parsedAuthKey.tt;
        rtn = parsedAuthKey;
        
      } 
    
    
    if(mt != '' && Math.round(nowUTC) < parseInt(tt)) {
      
        // *** The additional, secondary, server-side check, to validate the token, should go here *** // 

        //console.log(mt, tp, tt);
        const authMember = await getAuthorizedMember(mt);
        
        if (!authMember) {
            await Preferences.remove({key: 'solowise'});  
            return false;
        } else {
            await Preferences.set({ key: 'solowise', value: JSON.stringify(rtn)});
        }

        await getUserTimezone();
        await getUserIANATimezone();
      
        /*try {
            const formData = new FormData();
            formData.append('mt', mt);
            formData.append('tp', tp);
            formData.append('tt', tt);
            const response = await fetch('https://api.besolowise.com/members/authenticate/', {
                method: 'POST',
                credentials: 'include',
                body: formData
            });
            
            if(response.ok) {
                const obj = await response.json(); 
                //console.log('ok');
                console.log(obj);
                if(obj.success) {              
                    setAuthKey({ 'mt': mt, 'tp': tp, 'tt': tt });
                    await getAuthorizedMember(mt);
                    await getUserTimezone();
                    rtn = {mt: mt, tp: tp, tt: tt};
                    //console.log(rtn);
                } else {
                    setAuthKey({});
                    await Preferences.remove({key: 'solowise'});  
                    rtn = {mt: '', tp: '', tt: '0'};
                }
            } else {
                setAuthKey({});
                await Preferences.remove({key: 'solowise'});  
                rtn = {mt: '', tp: '', tt: '0'};
            }
        } catch(error) {
            setAuthKey({});
            await Preferences.remove({key: 'solowise'});  
            console.log(error);
        }*/
    } else {
        await Preferences.remove({key: 'solowise'});  
        rtn = {mt: '', tp: '', tt: '0'};
    }

    return rtn; 

  };


  //
  // Get the Authenticated Member
  //
  const getAuthorizedMember = async (mt: string) => {  
    /*const netstatus = await Network.getStatus();
    let connected = netstatus.connected;
    if (!connected) return;*/

    const platform = Capacitor.getPlatform();

    if (mt !== undefined) { 
        const formData = new FormData();
        formData.append('mt', mt);
        try {
            const response = await fetch('https://api.besolowise.com/members/get/', {
                method: 'POST',
                credentials: 'include',
                body: formData
            });
            
            if(response.ok) {
                const obj = await response.json();
                
                if(obj.memberStatus === '2' && obj.memberToken) {
                    setAuthorizedMember(obj); 
                    setNetworkStatus(true);

                    const levels = await getLevels(obj.memberToken);

                    setLevel(levels[0]);

                    let limits = {
                        contacts: levels[0].memberLevelContacts,
                        kids: levels[0].memberLevelKids,
                        partners: levels[0].memberLevelPartners,
                        pets: levels[0].memberLevelPets,
                        places: levels[0].memberLevelPlaces,
                        routes: levels[0].memberLevelRoutes,
                        timers: levels[0].memberLevelTimersInt,
                        vehicles: levels[0].memberLevelVehicles
                    };

                    setLimits(limits);

                    // 1) Fetch the plan(level) catalog from backend
                    const planCatalog: Plan[] = levels;       
                    setCatalog(planCatalog);

                    // 2) Fetch RC offering
                    if (platform === 'android' || platform === 'ios') {
                        const isReady = await Purchases.isConfigured();
                        if (!isReady) return false; 
                    }
                    
                    // 3) Detect current plan via active entitlements (pick the highest level whose primary entitlement is active)
                    const info = await billing.getCustomerInfo();

                    const displayName = [obj.memberFirstName, obj.memberLastName]
                        .filter(Boolean)
                        .join(' ');

                    await billing.setAttributes?.({
                        ...(obj.memberEmail ? { '$email': obj.memberEmail } : {}),
                        ...(displayName ? { '$displayName': displayName } : {}),
                        ...(obj.memberFirstName ? { firstName: obj.memberFirstName } : {}),
                        ...(obj.memberLastName ? { lastName: obj.memberLastName } : {}),
                    });

                    const active = info.entitlements.active;
                    
                    const isFree = Object.keys(active).length === 0;

                    const activePlans = planCatalog.filter(p => !!active[p.memberLevelRCEntitlement]);
                    
                    const current = activePlans.sort((a, b) => b.memberLevelTier - a.memberLevelTier)[0] ?? null;

                    setCurrentPlanKey(current?.memberLevelID ?? '1');
                    
                    if (!isFree) {  
                        levels.forEach((l: any) => { 
                        
                            if (l.memberLevelRCEntitlement === current.memberLevelRCEntitlement) {  
                                setLevel(l);

                                limits = {
                                    contacts: l.memberLevelContacts,
                                    kids: l.memberLevelKids,
                                    partners: l.memberLevelPartners,
                                    pets: l.memberLevelPets,
                                    places: l.memberLevelPlaces,
                                    routes: l.memberLevelRoutes,
                                    timers: l.memberLevelTimersInt,
                                    vehicles: l.memberLevelVehicles
                                };

                                setLimits(limits);
                            }
                        });
                    }

                    setAccountSetupPct(obj.accountSetupPct);
                    setSettings(obj.settings);  
                    await Preferences.set({ key: 'theme', value: obj.settings.theme}); 
                    setProfiles(obj.profiles);
                    setAlarms(obj.alarms);
                    initializeDarkMode(obj.settings.theme == '1');
                    
                    return obj;
                } else {
                    showToast({
                        message: 'Data Error 1: ' + obj.message,
                        duration: 3000,
                        color: 'danger',
                        position: 'middle'
                    });
                    return false;
                }
            } else {
                showToast({
                    message: 'Response Error. Please try again.',
                    duration: 3000,
                    color: 'danger',
                    position: 'middle'
                });
                return false;
            }
        } catch(error) {
            /*showToast({
                message: 'Connection Error 11: ' + error + '. Please try again.',
                duration: 3000,
                color: 'danger',
                position: 'middle'
            });*/
            console.log(error);
            //getAuthorizedMember(mt);
            //return false;
        } 
    }      
  };


  //
  // Initialize RevenueCat
  //
  useEffect(() => {
    const initRevenueCat = async () => {
        if (!authorizedMember.memberToken) return;

        try {
            setRevenueCatReady(false);
            await configureRevenueCat(authorizedMember.memberToken);
            setRevenueCatReady(true);
        } catch (e) {
            console.warn('RevenueCat initialization failed:', e);
            setRevenueCatReady(false);
        }
    };
    
    initRevenueCat();
}, [authorizedMember?.memberToken]);


  // 
  // Update Member
  //
  const updateMember = async (e: any) => {
    e.preventDefault();
    const form = e.target; 
    const formName = form.name;
    const btn: any = document.getElementById(formName + 'Submit');
    btn.innerHTML = 'Saving...';
    btn.setAttribute('disabled', '');
    const formData = new FormData(form);
    formData.append('mt', authKey.mt);
    formData.append('formName', formName);
    try {
        const apiURI = 'https://api.besolowise.com/members/update/';
        const response = await fetch(apiURI, {
            method: 'POST',
            credentials: 'include',
            body: formData
        });
        if(response.ok) {
            const obj = await response.json(); 
            //console.log(obj);
            if(obj.success) {
                //if (formName == 'emailForm') {
                    //memberLogOut();
                //}
                getAuthorizedMember(obj.mt);
                showToast({
                    message: 'SAVED. Your account was updated.',
                    duration: 2000,
                    color: 'success',
                });
            } else {
                showToast({
                    message: 'Data Error: ' + obj.message,
                    duration: 3500,
                    color: 'danger',
                });
            }
        } else {
            showToast({
                message: 'Response Error. Try again.',
                duration: 2000,
                color: 'danger',
                position: 'bottom'
            });  
        }  
         
    } catch(error) {
        showToast({
            //message: 'Connection Error 22: ' + error + '. Try again.',
            message: 'No Connection',
            duration: 3000,
            color: 'danger',
            position: 'bottom'
        });
    } 

    btn.innerHTML = 'Save';
    btn.removeAttribute('disabled');
    document.getElementById(formName + 'Cancel')?.click();           
 };


  //
  // Get the Member Settings
  //
  const getSettings = async (mt: string) => {
      try {
          const formData = new FormData();
          formData.append('mt', mt);
          const response = await fetch('https://api.besolowise.com/members/settings/', {
              method: 'POST',
              credentials: 'include',
              body: formData
          });
          if(response.ok) {   
              const obj = await response.json();
              
              if(obj.success) {
                  //console.log(obj.data);
                  setSettings(() => obj.data);
                  initializeDarkMode(obj.data.theme == '1');
                  await Preferences.set({ key: 'theme', value: obj.data.theme});
                  return true;
              } else {
                  showToast({
                      message: 'Data Error 2. Please try again.',
                      duration: 3000,
                      color: 'danger',
                      position: 'bottom'
                  });
              }
          } else {
              showToast({
                  message: 'Response Error. Please try again.',
                  duration: 3000,
                  color: 'danger',
                  position: 'bottom'
              });
          }
      } catch(error) {
          showToast({
              //message: 'Connection Error 33: ' + error + '. Please try again.',
              message: 'No Connection',
              duration: 30000,
              color: 'danger',
              position: 'bottom'
          });
      } 
  };

  
    //
    // Check/uncheck the toggle and update the theme based on isDark
    //
    const initializeDarkMode = (shouldAdd: boolean) => {
        let pagePath = window.location.pathname;
        if(pagePath != '/onboarding') document.body.classList.toggle('dark', shouldAdd);
    };


    //
    // Get Member Levels
    //  
    const getLevels = async (mt: string) => {
        try {
            const formData = new FormData();
            formData.append('mt', mt);
            formData.append('levels', 'all');
            const response = await fetch('https://api.besolowise.com/levels/get/', {
                method: 'POST',
                credentials: 'include',
                body: formData
            });
            if(response.ok) {
                const obj = await response.json(); 
                setLevels(obj);
                return obj;
            } else {
                showToast({
                    message: 'Response Error: Couldn\'t get the member levels list. Please try again.',
                    duration: 3000,
                    color: 'danger',
                    position: 'bottom'
                });
            }
        } catch(error) {
            showToast({
                //message: 'Connection Error: Couldn\'t get the member levels list. ' + error + '. Please try again.',
                message: 'No Connection',
                duration: 3000,
                color: 'danger',
                position: 'bottom'
            });
        } 
    };

    const getLevel = async (mt: string, id: any) => {
        try {
            const formData = new FormData();
            formData.append('mt', mt);
            formData.append('levelID', id);
            const response = await fetch('https://api.besolowise.com/levels/get/', {
                method: 'POST',
                credentials: 'include',
                body: formData
            });
            
            if(response.ok) {
                const obj = await response.json(); 
                return obj;
            } else {
                showToast({
                    message: 'Response Error: Couldn\'t get the member level info. Please try again.',
                    duration: 3000,
                    color: 'danger',
                    position: 'bottom'
                });
            }
        } catch(error) {
            showToast({
                //message: 'Connection Error: Couldn\'t get the member level info. ' + error + '. Please try again.',
                message: 'No Connection',
                duration: 3000,
                color: 'danger',
                position: 'bottom'
            });
        } 
    };


    //
    // Get all Countries
    //  
    const getCountries = async () => {
        try {
            const response = await fetch('https://api.besolowise.com/geolocation/?countries=all', {
                method: 'GET',
                credentials: 'include'
            });
            if(response.ok) {
                const obj = await response.json(); 
                setCountries(obj);
            } else {
                showToast({
                    message: 'Response Error: Couldn\'t get the countries list. Please try again.',
                    duration: 3000,
                    color: 'danger',
                    position: 'bottom'
                });
            }
        } catch(error) {
            showToast({
                //message: 'Connection Error: Couldn\'t get the countries list. ' + error + '. Please try again.',
                message: 'No Connection',
                duration: 3000,
                color: 'danger',
                position: 'bottom'
            });
        } 
    };


    //
    // Get all US States
    //
    const getStates = async () => {
        try {
            const response = await fetch('https://api.besolowise.com/geolocation/?states=all', {
                method: 'GET',
                credentials: 'include'
            });
             if(response.ok) {
                const obj = await response.json(); 
                setStates(obj);
            } else {
                showToast({
                    message: 'Response Error: Couldn\'t get the states list. Please try again.',
                    duration: 3000,
                    color: 'danger',
                    position: 'bottom'
                });
            }
        } catch(error) {
            showToast({
                //message: 'Connection Error: Couldn\'t get the states list. ' + error + '. Please try again.',
                message: 'No Connection',
                duration: 5000,
                color: 'danger',
                position: 'bottom'
            });
        } 
    };


    //
    // Get All Timezones
    //
    const getTimezones = async () => {
        try {
            const response = await fetch('https://api.besolowise.com/geolocation/?timezones=all', {
                method: 'GET',
                credentials: 'include'
            });
            if(response.ok) {
                const obj = await response.json(); 
                setTimezones(obj);
            } else {
                showToast({
                    message: 'Response Error: Couldn\'t get the timezones list. Please try again.',
                    duration: 3000,
                    color: 'danger',
                    position: 'bottom'
                });
            }
        } catch(error) {
            showToast({
                //message: 'Connection Error: Couldn\'t get the timezones list. ' + error + '. Please try again.',
                message: 'No Connection',
                duration: 3000,
                color: 'danger',
                position: 'bottom'
            });
        }     
    };


    //
    // Get All Timezones with IANA data
    //
    const getTimezonesIANA = async () => {
        try {
            const response = await fetch('https://api.besolowise.com/geolocation/?timezones=iana', {
                method: 'GET',
                credentials: 'include'
            });
            if(response.ok) {
                const obj = await response.json(); 
                setTimezonesIANA(obj);
            } else {
                showToast({
                    message: 'Response Error: Couldn\'t get the timezones list. Please try again.',
                    duration: 3000,
                    color: 'dark',
                    position: 'bottom'
                });
            }
        } catch(error) {
            showToast({
                //message: 'Connection Error: Couldn\'t get the timezones list. ' + error + '. Please try again.',
                message: 'No Connection',
                duration: 3000,
                color: 'dark',
                position: 'bottom'
            });
        }     
    };


    //
    // Get Timezone name from value
    //
    const timezoneName = (s: string) => {
        const selectedZone = timezones.find((zone: any) => s === zone[0]);
        if(selectedZone) return selectedZone[1];
        return ''; 
    };
    

    //
    // Get Timezone IANA name from value
    //
    const timezoneIANAName = (s: string) => {
        const selectedZone = timezonesIANA.find((zone: any) => s === zone['iana']);
        if(selectedZone) return selectedZone['label'];
        return ''; 
    };


    //
    // Check if Today is DST (returns true or false)
    //
    const getTodayIsDST = () => {
        const now = new Date();
        const januaryOffset = new Date(now.getFullYear(), 0, 1).getTimezoneOffset();
        const julyOffset = new Date(now.getFullYear(), 6, 1).getTimezoneOffset();    
        return now.getTimezoneOffset() !== januaryOffset;
    };


    //
    // Calculate Current Local Time Zone, including DST
    //
    const getUserTimezone = async () => { 
        const now = new Date();
        const tzMinutes = now.getTimezoneOffset(); 
        let tzHours = tzMinutes / 60;
        const todayIsDST = getTodayIsDST();
        if(todayIsDST) tzHours = tzHours + 1;

        const tzHoursStr = tzHours < 10 ? '0' + tzHours.toString() : tzHours.toString();     

        let tzStr = tzHoursStr + ':00';
        if(tzHours > 0) tzStr = '-' + tzStr;
        setUserTimezone(tzStr);
    };


    //
    // Get User's IANA time zone, including DST
    //
    const getUserIANATimezone = async () => { 
        const now = new Date();
        const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        setUserIANATimezone(userTimezone);
    };


    //
    // Get all current Timers
    //
    const getCurrentTimers = async (mt: any) => {
        try {
            const formData = new FormData();
            formData.append('mt', mt);
            formData.append('timers', 'all-current');
            const response = await fetch('https://api.besolowise.com/timers/get/', {
                method: 'POST',
                credentials: 'include',
                body: formData
            });

            if(response.ok) {                   
                const obj = await response.json();
                //console.log(obj);

                setCurrentTimers(obj);
                if (obj.length >= 1) {
                    //console.log(obj[0]);
                    setCurrentTimer(obj[0]);
                    setCurrentTimerEnd(new Date(obj[0].endTimeISO).getTime());
                    setCurrentTimerStatus(obj[0].status);
                }
                return obj;
            } else {
                showToast({
                    message: 'Response Error. Refresh this page to try again.',
                    duration: 3000,
                    color: 'danger',
                    position: 'bottom'
                });
            }
        } catch(error) {
            showToast({
                //message: 'Connection Error: ' + error + '. Refresh this page to try again.',
                message: 'No Connection',
                duration: 3000,
                color: 'danger',
                position: 'bottom'
            });
        } 
    };


    //
    // Get the Member's Places
    //
    const getPlaces = async (mt: string) => {
        try {
            const formData = new FormData();
            formData.append('mt', mt);
            formData.append('places', 'all');
            const response = await fetch('https://api.besolowise.com/places/get/', {
                method: 'POST',
                credentials: 'include',
                body: formData
            });

            if(response.ok) {   
                const obj = await response.json();  
                //console.log(obj);
                setPlaces(obj);
                setStartPlaces(obj);
                setEndPlaces(obj);
                return obj;
            } else {
            showToast({
                    message: 'Response Error. Please try again.',
                    duration: 2000,
                    color: 'danger',
                    position: 'bottom'
                });
            }
        } catch(error) {
            showToast({
                //message: 'Connection Error 44: ' + error + '.',
                message: 'No Connection',
                duration: 2000,
                color: 'danger',
                position: 'bottom'
            });
        } 
    };


    //
    // Get the Member's Saved Places
    //
    const getSavedPlaces = async (mt: string, limit: string) => {
        try {
            const formData = new FormData();
            formData.append('mt', mt);
            formData.append('places', 'saved');
            formData.append('limit', limit);
            const response = await fetch('https://api.besolowise.com/places/get/', {
                method: 'POST',
                credentials: 'include',
                body: formData
            });

            if(response.ok) {   
                const obj = await response.json();  
                //console.log(obj);
                setSavedPlaces(obj);
                //setStartPlaces(obj);
                //setEndPlaces(obj);
                return obj;
            } else {
            showToast({
                    message: 'Response Error. Please try again.',
                    duration: 2000,
                    color: 'danger',
                    position: 'bottom'
                });
            }
        } catch(error) {
            showToast({
                //message: 'Connection Error 44: ' + error + '.',
                message: 'No Connection',
                duration: 2000,
                color: 'danger',
                position: 'bottom'
            });
        } 
    };


    //
    // Get the Member's Routes
    //
    const getRoutes = async (mt: string) => {
        try {
            const formData = new FormData();
            formData.append('mt', mt);
            formData.append('routes', 'all');
            const response = await fetch('https://api.besolowise.com/routes/get/', {
                method: 'POST',
                credentials: 'include',
                body: formData
            });

            if(response.ok) {   
                const obj = await response.json();  
                //console.log(obj);
                setRoutes(obj);
            } else {
                showToast({
                    message: 'Response Error.',
                    duration: 2000,
                    color: 'danger',
                    position: 'bottom'
                });
            }
        } catch(error) {
            showToast({
                message: 'No Connection',
                duration: 2000,
                color: 'danger',
                position: 'bottom'
            });
        } 
    };


    //
    // Get the Member's Saved Routes
    //
    const getSavedRoutes = async (mt: string, limit: string) => {
        try {
            const formData = new FormData();
            formData.append('mt', mt);
            formData.append('routes', 'saved');
            formData.append('limit', limit);
            const response = await fetch('https://api.besolowise.com/routes/get/', {
                method: 'POST',
                credentials: 'include',
                body: formData
            });

            if(response.ok) {   
                const obj = await response.json();  
                //console.log(obj);
                setSavedRoutes(obj);
            } else {
                showToast({
                    message: 'Response Error.',
                    duration: 2000,
                    color: 'danger',
                    position: 'bottom'
                });
            }
        } catch(error) {
            showToast({
                message: 'No Connection',
                duration: 2000,
                color: 'danger',
                position: 'bottom'
            });
        } 
    };


    //
    // Get the Member's Partners
    //
    const getPartnerships = async (mt: string, cat: any) => {
        try {
            const formData = new FormData();
            formData.append('mt', mt);
            formData.append('partners', cat);
            const response = await fetch('https://api.besolowise.com/partners/get/', {
                method: 'POST',
                credentials: 'include',
                body: formData
            });
            
            if(response.ok) {   
                const json:any = await response.json();  
                setActivePartners(json[0]);
                setPendingPartners(json[1]);
            } else {
                showToast({
                    message: 'Response Error.',
                    duration: 2000,
                    color: 'danger',
                    position: 'bottom'
                });
            }
        } catch(error) {
            console.log(error);
            showToast({
                message: 'No Connection',
                duration: 2000,
                color: 'danger',
                position: 'bottom'
            });
        } 
    };


    //
    // Get the Member's Kids
    //
    const getKids = async (mt: string, limit: string) => {
        try {
            const formData = new FormData();
            formData.append('mt', mt);
            formData.append('kids', 'all');
            formData.append('limit', limit);
            const response = await fetch('https://solowise-api-obtg2.ondigitalocean.app/kids/get/', {
                method: 'POST',
                credentials: 'include',
                body: formData
            });
            if(response.ok) {   
                const json = await response.json();  
                setKids(json);
                setAvailableKids(json);
            } else {
                showToast({
                    message: 'Response Error. Please try again.',
                    duration: 3000,
                    color: 'danger',
                    position: 'bottom'
                });
            }
        } catch(error) {
            showToast({
                //message: 'Connection Error 77: ' + error + '. Please try again.',
                message: 'No Connection',
                duration: 3000,
                color: 'danger',
                position: 'bottom'
            });
        } 
    };


    //
    // Get the Member's Pets
    //
    const getPets = async (mt: string, limit: string) => {
        try {
            const formData = new FormData();
            formData.append('mt', mt);
            formData.append('pets', 'all');
            formData.append('limit', limit);
            const response = await fetch('https://api.besolowise.com/pets/get/', {
                method: 'POST',
                credentials: 'include',
                body: formData
            });
            if(response.ok) {   
                const json = await response.json();  
                setPets(json);
                setAvailablePets(json);
            } else {
                showToast({
                    message: 'Response Error. Please try again.',
                    duration: 3000,
                    color: 'danger',
                    position: 'bottom'
                });
            }
        } catch(error) {
            showToast({
                //message: 'Connection Error 88: ' + error + '. Please try again.',
                message: 'No Connection',
                duration: 2000,
                color: 'danger',
                position: 'bottom'
            });
        } 
    };


    //
    // Get the Member's Vehicles
    //
    const getVehicles = async (mt: string, limit: string) => {
        try {
            const formData = new FormData();
            formData.append('mt', mt);
            formData.append('vehicles', 'all');
            formData.append('limit', limit);
            const response = await fetch('https://api.besolowise.com/vehicles/get/', {
                method: 'POST',
                credentials: 'include',
                body: formData
            });
            if(response.ok) {   
                const json = await response.json();  
                setVehicles(json);
                setVehiclesAll(json);
                setVehiclesStart(json);
                setVehiclesEnroute(json);
                setVehiclesEnd(json);
            } else {
                showToast({
                    message: 'Response Error. Please try again.',
                    duration: 3000,
                    color: 'danger',
                    position: 'bottom'
                });
            }
        } catch(error) {
            showToast({
                //message: 'Connection Error 99: ' + error + '. Please try again.',
                message: 'No Connection',
                duration: 2000,
                color: 'danger',
                position: 'bottom'
            });
        } 
    };


    //
    // Get the Member's Contacts
    //
    const getContacts = async (mt: string, cat: any, limit: string) => {
        try {
            //console.log(limit);
            const formData = new FormData();
            formData.append('mt', mt);
            formData.append('contacts', cat);
            formData.append('limit', limit);
            const response = await fetch('https://api.besolowise.com/contacts/get/', {
                method: 'POST',
                credentials: 'include',
                body: formData
            });

            if(response.ok) {   
                const obj = await response.json();  
                setContacts(obj);
                setAvailableContacts(obj);
                const entries = Object.entries(obj);
                let newChecked;
                newChecked = checkedContacts.length > 0 ? [...checkedContacts] : [];
                if(entries.length > 0) {
                    entries.forEach((item: any, index, arr) => {
                        if(item[1].contactAlwaysUse == '1') newChecked.push(item[1].contactToken);                    
                    }); 
                }
                setCheckedContacts(newChecked);
            } else {
                showToast({
                    message: 'Response Error. Please try again.',
                    duration: 3000,
                    color: 'danger',
                    position: 'bottom'
                });
            }
        } catch(error) {
            showToast({
                //message: 'Connection Error 100: ' + error + '. Please try again.',
                message: 'No Connection',
                duration: 2000,
                color: 'danger',
                position: 'bottom'
            });
        } 
    };


    const fadeInFullLogo = () => {
        setFullLogoLoaded(true);
    };


    const fadeInTextLogo = () => {
        setTextLogoLoaded(true);
    };


    const fadeInImage = (e: any) => {
        e.target.classList.add('fade-in');
        e.target.classList.remove('fade-out');
    };

    
    const nl2br = (str: string) => {
        if (typeof str === 'undefined' || str === null) return '';
        let breakTag = '<br>';
        return (str + '').replace(/([^>\r\n]?)(\r\n|\n\r|\r|\n)/g, '$1' + breakTag + '$2');
    };


    const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];    


    const clearAllCookies = () => {
        const cookies = document.cookie.split(";");

        for (let i = 0; i < cookies.length; i++) {
            const cookie = cookies[i];
            const eqPos = cookie.indexOf("=");
            const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
            
            // Attempt deletion across common domain variations and paths
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;`;
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${location.hostname};`;
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${location.hostname.split('.').slice(-2).join('.')};`;
        }
    };


    return (
        <AppContext.Provider value={{ networkStatus, setNetworkStatus, appStatus, setAppStatus, appStatusMarkup, setAppStatusMarkup, getAppStatus, authKey, setAuthKey, authenticateMember, authorizedMember, getAuthorizedMember, accountSetupPct, setAccountSetupPct, revenueCatReady, loading, setLoading, showLoader, setShowLoader, setAuthorizedMember, updateMember, level, alarms, setAlarms, getLevel, setLevel, levels, getLevels, setLevels, limits, setLimits, profiles, setProfiles, settings, getSettings, setSettings, countries, getCountries, states, getStates, places, getPlaces, setPlaces, savedPlaces, getSavedPlaces, setSavedPlaces, startPlaces, setStartPlaces, endPlaces, setEndPlaces, routes, getRoutes, setRoutes, savedRoutes, getSavedRoutes, setSavedRoutes, activePartners, getPartnerships, setActivePartners, pendingPartners, setPendingPartners, kids, getKids, setKids, availableKids, setAvailableKids, pets, getPets, setPets, availablePets, setAvailablePets, vehicles, getVehicles, setVehicles, vehiclesAll, setVehiclesAll, vehiclesStart, setVehiclesStart, selectedVehiclesStart, setSelectedVehiclesStart, vehiclesEnroute, setVehiclesEnroute, selectedVehiclesEnroute, setSelectedVehiclesEnroute, vehiclesEnd, setVehiclesEnd, selectedVehiclesEnd, setSelectedVehiclesEnd, contacts, availableContacts, getContacts, setContacts, setAvailableContacts, checkedContacts, setCheckedContacts, locationPerm, setLocationPerm, locationPermStr, setLocationPermStr, pushPermStr, setPushPermStr, now, setNow, currentTimer, setCurrentTimer, currentTimerEnd, setCurrentTimerEnd, currentTimerStatus, setCurrentTimerStatus, currentTimers, setCurrentTimers, getCurrentTimers, pendingTimer, setPendingTimer, activeContactsCount, setActiveContactsCount, activeKidsCount, setActiveKidsCount, activePartnershipsCount, setActivePartnershipsCount, activePetsCount, setActivePetsCount, activePlacesCount, setActivePlacesCount, activeRoutesCount, setActiveRoutesCount, activeVehiclesCount, setActiveVehiclesCount, monthlyAlertsCount, setMonthlyAlertsCount, fullLogoLoaded, textLogoLoaded, setFullLogoLoaded, setTextLogoLoaded, fadeInFullLogo, fadeInTextLogo, fadeInImage, nl2br, clearAllCookies, SWTextLogo, SWTextDarkLogo, SWFullHorizLogo, timezones, timezonesIANA, timezoneName, timezoneIANAName, getTimezones, getTimezonesIANA, setTimezones, userTimezone, userIANATimezone, getUserTimezone, getUserIANATimezone, setUserTimezone, setUserIANATimezone, monthNames, Preferences, swipeEnabled, setSwipeEnabled, showToast, present, dismiss }}>
        {children}
        </AppContext.Provider>
    );
};

export const useAppContext = (): AppContextProps => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};
